import { PostResponse } from "../poshmark";
import { formatCurrencyCompact } from "./formatters";

export type TrendAnalysis = {
  query: string;
  coreMetrics: CoreMetrics;
  stats: Stat[];
  filters?: PostFilters;
  availablePosts: Array<{ picture_url: string }>;
  soldPosts: Array<{ picture_url: string }>;
  score?: number;
}

export interface PostFilters {
  brands?: string[];
  categories?: string[];
  sizes?: string[];
  colors?: string[];
  styles?: string[];
  departments?: string[];
}

interface Color {
  name: string;
}

export interface CoreMetrics {
  soldCounts: {
    last30Days: number;
    last60Days: number;
    last90Days: number;
  };
  totalListings: {
    last30Days: number;
    last60Days: number;
    last90Days: number;
  };
  sellThroughRates: {
    last30Days: number;
    last90Days: number;
  };
  saturationRate: number;
  averageTimeToSell: number;
  averageSoldPrice: number;
  averageAvailablePrice: number;
  availablePostsCount: number;
};

export type Stat = {
  title: string;
  mainValue: number | string;
  secondaryValue?: number | string;
  mainLabel?: string;
  secondaryLabel?: string;
  description?: string;
  insight?: {
    label: string;
    level: 'success' | 'warning' | 'danger';
  };
};

export function getTrendAnalysis(query: string, soldPosts: PostResponse[], availablePosts: PostResponse[], filters: PostFilters): TrendAnalysis {

  const filteredSold = getFilteredPosts(soldPosts, filters);
  const filteredAvailable = getFilteredPosts(availablePosts, filters);
  const coreMetrics = calculateCoreMetrics(filteredSold, filteredAvailable);
  const stats = calculateStats(coreMetrics);

  return {
    query,
    coreMetrics,
    stats,
    filters,
    availablePosts: filteredAvailable.map(post => ({ picture_url: post.picture_url })),
    soldPosts: filteredSold.map(post => ({ picture_url: post.picture_url })),
  };
}

interface DateRanges {
  thirtyDaysAgo: Date;
  sixtyDaysAgo: Date;
  ninetyDaysAgo: Date;
}

function calculateDateRanges(): DateRanges {
  const thirtyDaysAgo = new Date();
  thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

  const sixtyDaysAgo = new Date();
  sixtyDaysAgo.setDate(sixtyDaysAgo.getDate() - 60);

  const ninetyDaysAgo = new Date();
  ninetyDaysAgo.setDate(ninetyDaysAgo.getDate() - 90);

  return { thirtyDaysAgo, sixtyDaysAgo, ninetyDaysAgo };
}

function calculateCoreMetrics(soldPosts: PostResponse[], availablePosts: PostResponse[]): CoreMetrics {
  // Calculate date ranges
  const dateRanges = calculateDateRanges();

  // Calculate sold counts for different time periods
  const soldCounts = {
    last30Days: countSoldInPeriod(soldPosts, dateRanges.thirtyDaysAgo),
    last60Days: countSoldInPeriod(soldPosts, dateRanges.sixtyDaysAgo),
    last90Days: countSoldInPeriod(soldPosts, dateRanges.ninetyDaysAgo)
  };

  // Calculate total listings (sold + available)
  const totalListings = {
    last30Days: soldCounts.last30Days + availablePosts.length,
    last60Days: soldCounts.last60Days + availablePosts.length,
    last90Days: soldCounts.last90Days + availablePosts.length
  };

  // Calculate sell-through rates
  const sellThroughRates = {
    last30Days: calculateSellThroughRate(soldCounts.last30Days, totalListings.last30Days),
    last90Days: calculateSellThroughRate(soldCounts.last90Days, totalListings.last90Days)
  };

  // Calculate saturation rate
  const saturationRate = calculateSaturationRate(availablePosts.length, soldCounts.last90Days);

  // Calculate averages
  const averageTimeToSell = calculateAverageTimeToSell(soldPosts);
  const averageSoldPrice = calculateAveragePrice(soldPosts);
  const averageAvailablePrice = calculateAveragePrice(availablePosts);

  return {
    soldCounts,
    totalListings,
    sellThroughRates,
    saturationRate,
    averageTimeToSell,
    averageSoldPrice,
    averageAvailablePrice,
    availablePostsCount: availablePosts.length
  };
}

function countSoldInPeriod(soldPosts: PostResponse[], startDate: Date): number {
  return soldPosts.filter(post => {
    const saleDate = new Date(post.inventory?.status_changed_at);
    return saleDate >= startDate;
  }).length;
}

function calculateSellThroughRate(soldCount: number, totalListings: number): number {
  return totalListings > 0 ? Math.round((soldCount / totalListings) * 100) : 0;
}

function calculateSaturationRate(availableCount: number, soldCount90Days: number): number {
  return availableCount && soldCount90Days
    ? Math.round((availableCount / soldCount90Days) * 100)
    : 0;
}

function calculateAverageTimeToSell(soldPosts: PostResponse[]): number {
  const totalTimeToSell = soldPosts.reduce((acc, post) => {
    const timeToSell = calculateTimeToSell(
      post.inventory?.status_changed_at,
      post.first_published_at
    );
    return timeToSell ? acc + timeToSell : acc;
  }, 0);

  return soldPosts.length > 0 ? totalTimeToSell / soldPosts.length : 0;
}

function calculateAveragePrice(posts: PostResponse[]): number {
  const postsWithValidPrice = posts.filter(post => typeof post.price === 'number');

  if (postsWithValidPrice.length === 0) return 0;

  const totalPrice = postsWithValidPrice.reduce((acc, post) =>
    acc + (post.price as number), 0
  );

  return totalPrice / postsWithValidPrice.length;
}

export function calculateStats(metrics: CoreMetrics): Stat[] {
  return [
    {
      title: 'Sell-Through Rate',
      mainValue: `${metrics.sellThroughRates.last30Days}%`,
      secondaryValue: `${metrics.sellThroughRates.last90Days}%`,
      mainLabel: 'Last 30 Days',
      secondaryLabel: 'Last 90 Days',
      description: 'A key performance indicator showing how quickly items are selling. Rates above 75% within 90 days indicate a healthy market, while rates below 30% suggest challenging selling conditions.',
      insight: determineSellThroughInsight(metrics.sellThroughRates.last90Days)
    },
    {
      title: 'Recent Listings',
      mainValue: metrics.soldCounts.last90Days >= 500 ? '500+' : metrics.soldCounts.last90Days,
      secondaryValue: metrics.availablePostsCount >= 500 ? '500+' : metrics.availablePostsCount,
      mainLabel: 'Sold last 90 days',
      secondaryLabel: 'Available',
      description: 'Compare the volume of sold vs. available listings in the last 90 days. A healthy market typically shows more sold than available items. If available listings greatly outnumber sold ones, consider this a warning sign of slow-moving inventory.',
      insight: determineInsight(metrics.soldCounts.last90Days, metrics.availablePostsCount)
    },
    {
      title: 'Average Time to Sell',
      mainValue: `${Math.round(metrics.averageTimeToSell)} days`,
      mainLabel: 'Last 30 Days',
      description: 'The typical time items take to sell. Use this to set expectations for your inventory turnover and cash flow. Items taking longer than this average might need price adjustments or improved listings.',
      insight: determineTimeToSellInsight(metrics.averageTimeToSell)
    },
    {
      title: 'Market Saturation',
      mainValue: `${metrics.saturationRate}%`,
      mainLabel: 'Saturation',
      description: 'Ratio of available listings to sold listings. A high percentage (>100%) suggests a saturated market with more competition. A low percentage (<50%) indicates less competition and potentially faster sales.',
      insight: determineSaturationInsight(metrics.saturationRate)
    },
    {
      title: 'Average Price',
      mainValue: formatCurrencyCompact(metrics.averageSoldPrice),
      secondaryValue: formatCurrencyCompact(metrics.averageAvailablePrice),
      mainLabel: 'Sold',
      secondaryLabel: 'Available',
      description: 'Compare actual selling prices vs. current listing prices. If available prices are significantly higher than sold prices, the market may be overpriced. Consider pricing closer to the sold average for faster sales.',
      insight: determinePriceInsight(metrics.averageSoldPrice, metrics.averageAvailablePrice)
    }
  ];
}

function determineInsight(sold: number, available: number, threshold: number = 2): { label: string; level: 'success' | 'warning' | 'danger' } {
  const highDemandRatio = 1.5;
  if (sold > available * highDemandRatio) {
    return { label: 'High Demand', level: 'success' };
  }
  if (sold > available) {
    return { label: 'Good Demand', level: 'success' };
  }
  if (available > sold * threshold) {
    return { label: 'Oversaturated', level: 'danger' };
  }
  return { label: 'Balanced Market', level: 'warning' };
}

function determinePriceInsight(soldPrice: number, availablePrice: number): { label: string; level: 'success' | 'warning' | 'danger' } {
  if (availablePrice > soldPrice * 1.2) {
    return { label: 'Overpriced Market', level: 'danger' };
  }
  if (availablePrice < soldPrice * 0.8) {
    return { label: 'Underpriced Market', level: 'warning' };
  }
  return { label: 'Balanced Pricing', level: 'success' };
}

function determineTimeToSellInsight(timeToSell: number | null): { label: string; level: 'success' | 'warning' | 'danger' } {
  if (timeToSell === null) return { label: 'Insufficient Data', level: 'warning' };
  if (timeToSell <= 7) return { label: 'Very Fast Moving', level: 'success' };
  if (timeToSell <= 14) return { label: 'Fast Moving', level: 'success' };
  if (timeToSell <= 90) return { label: 'Moderate', level: 'warning' };
  return { label: 'Slow Moving', level: 'danger' };
}

function determineSellThroughInsight(rate: number): { label: string; level: 'success' | 'warning' | 'danger' } {
  if (rate >= 80) return { label: 'High Sell-Through', level: 'success' };
  if (rate >= 70) return { label: 'Good Sell-Through', level: 'success' };
  if (rate >= 55) return { label: 'Fair Sell-Through', level: 'warning' };
  return { label: 'Low Sell-Through', level: 'danger' };
}

function determineSaturationInsight(rate: number): { label: string; level: 'success' | 'warning' | 'danger' } {
  if (rate <= 30) return { label: 'Low Competition', level: 'success' };
  if (rate <= 75) return { label: 'Moderate Competition', level: 'warning' };
  if (rate <= 100) return { label: 'High Competition', level: 'danger' };
  return { label: 'Overly Competitive', level: 'danger' };
}

export function calculateTimeToSell(statusChangedAt: string, firstPublishedAt: string): number | null {
  const saleDate = new Date(statusChangedAt);
  const publishDate = new Date(firstPublishedAt);

  if (isNaN(saleDate.getTime()) || isNaN(publishDate.getTime())) {
    return null;
  }

  return Math.floor((saleDate.getTime() - publishDate.getTime()) / (1000 * 60 * 60 * 24));
}

export function matchesStyle(title: string, style: string): boolean {
  const titleWords = title.toLowerCase().split(/\s+/);
  const styleWords = style.toLowerCase().split(/\s+/);

  return styleWords.every(styleWord =>
    titleWords.some(titleWord => titleWord.includes(styleWord))
  );
};

export function getFilteredPosts(posts: any[], filters?: PostFilters): any[] {
  if (!posts) return [];
  if (!filters) return posts;

  return posts.filter(post => {
    const brandMatch = !filters.brands?.length ||
      (post.brand && filters.brands.some(brand =>
        brand.toLowerCase() === post.brand.toLowerCase()
      ));

    const categoryMatch = !filters.categories?.length ||
      (post.category_features?.[0]?.slug && filters.categories.some(category =>
        category.toLowerCase() === post.category_features[0].slug.toLowerCase()
      ));

    const sizeMatch = !filters.sizes?.length ||
      (post.size && filters.sizes.some(size =>
        size.toLowerCase().replace(/\s+/g, '-') === post.size.toLowerCase().replace(/\s+/g, '-')
      ));

    const colorMatch = !filters.colors?.length ||
      (post.colors && post.colors.some((color: Color) =>
        filters.colors?.some(filterColor =>
          filterColor.toLowerCase().replace(/\s+/g, '-') === color.name.toLowerCase().replace(/\s+/g, '-')
        )
      ));

    const styleMatch = !filters.styles?.length ||
      (post.title && filters.styles.some(style =>
        matchesStyle(post.title, style)
      ));

    const departmentMatch = !filters.departments?.length ||
      (post.department?.slug && filters.departments.some(department =>
        department.toLowerCase() === post.department.slug.toLowerCase()
      ));

    return brandMatch && categoryMatch && sizeMatch && colorMatch && styleMatch && departmentMatch;
  });
}

export function sortBy90DaySellThroughRate(analyses: TrendAnalysis[]): TrendAnalysis[] {
  return analyses.sort((a, b) => {
    const aSold = a.coreMetrics?.soldCounts?.last30Days ?? 0;
    const bSold = b.coreMetrics?.soldCounts?.last30Days ?? 0;
    const aTotal = a.coreMetrics?.totalListings?.last30Days ?? 0;
    const bTotal = b.coreMetrics?.totalListings?.last30Days ?? 0;

    // If a has only 1 sold or 1 total listing in last 30 days, push it down
    const aIsLow = aSold <= 1 || aTotal <= 1;
    const bIsLow = bSold <= 1 || bTotal <= 1;

    if (aIsLow && !bIsLow) return 1;
    if (!aIsLow && bIsLow) return -1;
    // Otherwise, sort by sellThroughRate
    return b.coreMetrics?.sellThroughRates?.last90Days - a.coreMetrics?.sellThroughRates?.last90Days;
  });
}

/**
 * Calculates a comprehensive score for a style based on multiple weighted metrics.
 * Returns a score between 0-100, where higher is better.
 */
export function calculateStyleScore(analysis: TrendAnalysis): number {
  const metrics = analysis.coreMetrics;
  if (!metrics) return 0;

  let totalScore = 0;
  let totalWeight = 0;

  // 1. Sell-through rate (30% weight) - Most important metric
  const sellThrough30Weight = 30;
  const sellThrough30Score = Math.min(metrics.sellThroughRates.last30Days, 100);
  totalScore += sellThrough30Score * sellThrough30Weight;
  totalWeight += sellThrough30Weight;

  // 2. Sell-through rate 90 days (20% weight) - Long-term performance
  const sellThrough90Weight = 20;
  const sellThrough90Score = Math.min(metrics.sellThroughRates.last90Days, 100);
  totalScore += sellThrough90Score * sellThrough90Weight;
  totalWeight += sellThrough90Weight;

  // 3. Sales volume (20% weight) - Higher sales = more demand
  const salesVolumeWeight = 20;
  const salesVolume30 = metrics.soldCounts.last30Days;
  const salesVolume90 = metrics.soldCounts.last90Days;
  
  // Normalize sales volume (0-100 scale)
  // 0 sales = 0 points, 10+ sales = 100 points
  const salesVolumeScore = Math.min(Math.max(salesVolume30 * 10, salesVolume90 / 10), 100);
  totalScore += salesVolumeScore * salesVolumeWeight;
  totalWeight += salesVolumeWeight;

  // 4. Market saturation penalty (15% weight) - Lower competition is better
  const saturationWeight = 15;
  const saturationRate = metrics.saturationRate;
  
  // Invert saturation rate: lower saturation = higher score
  // 0% saturation = 100 points, 100%+ saturation = 0 points
  const saturationScore = Math.max(0, 100 - saturationRate);
  totalScore += saturationScore * saturationWeight;
  totalWeight += saturationWeight;

  // 5. Time to sell (10% weight) - Faster selling = better
  const timeToSellWeight = 10;
  const timeToSell = metrics.averageTimeToSell;
  
  // Normalize time to sell (0-100 scale)
  // 0 days = 100 points, 90+ days = 0 points
  const timeToSellScore = Math.max(0, 100 - (timeToSell / 90) * 100);
  totalScore += timeToSellScore * timeToSellWeight;
  totalWeight += timeToSellWeight;

  // 6. Price stability (5% weight) - Similar sold vs available prices = stable market
  const priceStabilityWeight = 5;
  const soldPrice = metrics.averageSoldPrice;
  const availablePrice = metrics.averageAvailablePrice;
  
  let priceStabilityScore = 100;
  if (soldPrice > 0 && availablePrice > 0) {
    const priceDifference = Math.abs(soldPrice - availablePrice) / soldPrice;
    // 0% difference = 100 points, 50%+ difference = 0 points
    priceStabilityScore = Math.max(0, 100 - (priceDifference * 200));
  }
  totalScore += priceStabilityScore * priceStabilityWeight;
  totalWeight += priceStabilityWeight;

  // Penalties for insufficient data
  const penalties = calculateDataPenalties(metrics);
  totalScore -= penalties;

  // Normalize to 0-100 scale
  return totalWeight > 0 ? Math.max(0, Math.min(100, totalScore / totalWeight)) : 0;
}

/**
 * Applies penalties for insufficient data to avoid ranking styles with poor data quality
 */
export function calculateDataPenalties(metrics: CoreMetrics): number {
  let penalties = 0;

  // Penalty for very low sales volume (indicates unreliable data)
  if (metrics.soldCounts.last30Days <= 1) penalties += 20;
  if (metrics.soldCounts.last90Days <= 2) penalties += 15;

  // Penalty for very low total listings (small sample size)
  if (metrics.totalListings.last30Days <= 2) penalties += 25;
  if (metrics.totalListings.last90Days <= 3) penalties += 20;

  // Penalty for zero sell-through rates (no recent activity)
  if (metrics.sellThroughRates.last30Days === 0 && metrics.sellThroughRates.last90Days === 0) {
    penalties += 30;
  }

  // Penalty for excessive saturation (overly competitive market)
  if (metrics.saturationRate > 200) penalties += 15;

  return penalties;
}
